import { useState } from "react";
import Button from "@/components/Button";
import TextField from "@/components/TextField";
import FileUpload from "@/components/FileUpload";
import { Controller, useForm } from "react-hook-form";
import useModal from "@/hooks/useModal";
import useAmplitude from "@/hooks/useAmplitude";
import { useMutation } from "@apollo/client";
import { CREATE_EXPENSE_DOCUMENT_UPLOAD_MUTATION } from "@/pages/events/[eventSlug]/(tabbed)/payouts/queries";
import { useOutletContext, useParams } from "react-router-dom";
import { EventOutletContext } from "@/pages/events/[eventSlug]/(tabbed)/_layout";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function FileUploadModal({ isOpen, onClose, onSuccess }: FileUploadModalProps) {
  const {
    control,
    setValue,
    clearErrors,
    trigger,
    getValues,
    formState: { errors }
  } = useForm();

  const { close, Modal } = useModal();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUploadErrorMsg, setFileUploadErrorMsg] = useState("");
  const event = useOutletContext<EventOutletContext>();
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();
  const [uploadDocument, { loading }] = useMutation(CREATE_EXPENSE_DOCUMENT_UPLOAD_MUTATION);

  const handleFileUpload = async () => {
    try {
      const { data } = await uploadDocument({
        variables: {
          eventId: event.eventData?.id,
          file: selectedFile,
          name: getValues("expenseDocumentName")
        }
      });

      if (data) {
        trackEvent(
          EVENT_ANALYTICS_EVENTS.expenseDocumentUploaded,
          getEventAnalyticsData({
            source: "payouts",
            eventId: event.eventData?.id,
            eventSlug,
            action: "upload"
          })
        );
        onSuccess?.();
        close();
      }
    } catch (error) {
      setFileUploadErrorMsg("Error uploading document");
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      className="max-w-[660px] px-6 py-8"
      onClosed={onClose}
      headline={"Upload Document"}
      action={
        <Button variant="filled" onClick={handleFileUpload} testId="upload-expense-document-button">
          Save
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        <FileUpload
          loading={loading}
          accept=".pdf, .docx, .xlsx, .jpg, .png"
          subtitle="Allowed file extensions: pdf, docx, xlsx, jpg, png"
          handleUpload={(file) => {
            setValue("expenseDocumentName", file.name.replace(/\.[^/.]+$/, ""));
            setSelectedFile(file);
            setFileUploadErrorMsg("");
          }}
          errorMsg={fileUploadErrorMsg}
        />

        <Controller
          name="expenseDocumentName"
          control={control}
          rules={{
            required: "Document name is required"
          }}
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Document Name"
              value={value}
              onChange={(e) => {
                onChange(e);
                if ((e.target as HTMLInputElement).value && errors.expenseDocumentName) {
                  clearErrors("expenseDocumentName");
                }
                if (!(e.target as HTMLInputElement).value) {
                  trigger("expenseDocumentName");
                }
              }}
              required={true}
              error={!!errors.expenseDocumentName}
              errorText={errors.expenseDocumentName?.message as string}
            />
          )}
        />
      </div>
    </Modal>
  );
}
